import React, { PropsWithChildren } from 'react';
import { VerticalMenu } from '@common/components';

export function OrganizationAdminVerticalNavMenu({
  permissions,
  hideMenu,
  children,
}: PropsWithChildren<{
  hideMenu: boolean;
  children: React.ReactNode;
  permissions: PermissionsType;
}>) {
  const menuItems = getOrganizationAdminVerticalNavMenuItems(permissions);

  return (
    <VerticalMenu
      items={menuItems}
      menuKey="admin-settings-menu"
      hideMenu={hideMenu}
    >
      {children}
    </VerticalMenu>
  );
}

export const ORGANIZATION_ADMIN_MENU = {
  HOME: {
    LABEL: 'Admin',
    ROUTE: '/works/admin',
  },
  ALLOW_LISTS: {
    LABEL: 'Allow Lists',
    ROUTE: '/works/admin/allow-lists',
  },
  LOCATIONS: {
    LABEL: 'Locations',
    ROUTE: '/works/admin/locations',
  },
  GROUPS: {
    LABEL: 'Groups',
    ROUTE: '/works/admin/groups',
  },
  TEMPLATES__STAFFING_REQUEST: {
    LABEL: 'Staffing Request',
    ROUTE: '/works/admin/templates/staffing-request',
  },
  TEMPLATES__COMPLIANCE: {
    LABEL: 'Compliance',
    ROUTE: '/works/admin/templates/compliance-list',
  },
  APPROVALS: {
    LABEL: 'Approvals',
    ROUTE: '/works/admin/approvals',
  },
  RATES__INCENTIVES: {
    LABEL: 'Incentives',
    LABEL_SINGLE: 'Incentives',
    ROUTE: '/works/admin/rates/incentives',
  },
  RATES__LONG_TERM: {
    LABEL: 'Long Term',
    LABEL_SINGLE: 'Long Term Rates',
    ROUTE: '/works/admin/rates/long-term',
  },
  SUPPLIERS: {
    LABEL: 'Suppliers',
    ROUTE: '/works/admin/suppliers',
  },
  USERS: {
    LABEL: 'Users',
    ROUTE: '/works/admin/users',
  },
  BILLING: {
    LABEL: 'Billing',
    ROUTE: '/works/admin/billing',
  },
  TIMEKEEPING: {
    LABEL: 'Timekeeping',
    ROUTE: '/works/admin/timekeeping',
  },
};

type PermissionsType = {
  canShowAdminApprovalsItem: boolean;
  canShowAdminStaffingRequestTemplates: boolean;
  canShowAdminWorksListTemplates: boolean;
  canShowAdminRatesOnDemandTab: boolean;
  canShowAdminRatesLongTermTab: boolean;
  canShowAdminMembers: boolean;
  canShowAdminSuppliers: boolean;
  canShowAdminOrganizationStructure: boolean;
  canShowAdminTimekeepingHolidays: boolean;
  canShowAdminGroupsTab: boolean;
  canShowAdminBilling: boolean;
  canShowOrganizationOnDemandSettings: boolean;
};

function getOrganizationAdminVerticalNavMenuItems(
  permissions: PermissionsType
) {
  return [
    {
      name: ORGANIZATION_ADMIN_MENU.ALLOW_LISTS.LABEL,
      iconOfKind: { icon: 'hospitalUser', kind: 'light' },
      redirectTo: ORGANIZATION_ADMIN_MENU.ALLOW_LISTS.ROUTE,
      canShow: permissions.canShowOrganizationOnDemandSettings,
    },
    {
      name: ORGANIZATION_ADMIN_MENU.APPROVALS.LABEL,
      iconOfKind: { icon: 'circleCheck', kind: 'light' },
      redirectTo: ORGANIZATION_ADMIN_MENU.APPROVALS.ROUTE,
      canShow: permissions.canShowAdminApprovalsItem,
    },
    {
      name: ORGANIZATION_ADMIN_MENU.BILLING.LABEL,
      iconOfKind: { icon: 'fileInvoice', kind: 'light' },
      redirectTo: ORGANIZATION_ADMIN_MENU.BILLING.ROUTE,
      canShow: permissions.canShowAdminBilling,
    },
    {
      name: ORGANIZATION_ADMIN_MENU.GROUPS.LABEL,
      iconOfKind: { icon: 'peopleGroup', kind: 'light' },
      redirectTo: ORGANIZATION_ADMIN_MENU.GROUPS.ROUTE,
      canShow: permissions.canShowAdminGroupsTab,
    },
    {
      name: ORGANIZATION_ADMIN_MENU.LOCATIONS.LABEL,
      iconOfKind: { icon: 'hospital', kind: 'light' },
      redirectTo: ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE,
      canShow: permissions.canShowAdminOrganizationStructure,
    },
    {
      name: ORGANIZATION_ADMIN_MENU.RATES__INCENTIVES.LABEL_SINGLE,
      iconOfKind: { icon: 'dollarSign', kind: 'light' },
      redirectTo: ORGANIZATION_ADMIN_MENU.RATES__INCENTIVES.ROUTE,
      canShow:
        !permissions.canShowAdminRatesLongTermTab &&
        permissions.canShowAdminRatesOnDemandTab,
    },
    {
      name: ORGANIZATION_ADMIN_MENU.RATES__LONG_TERM.LABEL_SINGLE,
      iconOfKind: { icon: 'dollarSign', kind: 'light' },
      redirectTo: ORGANIZATION_ADMIN_MENU.RATES__LONG_TERM.ROUTE,
      canShow:
        permissions.canShowAdminRatesLongTermTab &&
        !permissions.canShowAdminRatesOnDemandTab,
    },
    {
      name: 'Rates',
      iconOfKind: { icon: 'dollarSign', kind: 'light' },
      children: [
        {
          name: ORGANIZATION_ADMIN_MENU.RATES__LONG_TERM.LABEL,
          redirectTo: ORGANIZATION_ADMIN_MENU.RATES__LONG_TERM.ROUTE,
        },
        {
          name: ORGANIZATION_ADMIN_MENU.RATES__INCENTIVES.LABEL,
          redirectTo: ORGANIZATION_ADMIN_MENU.RATES__INCENTIVES.ROUTE,
        },
      ],
      canShow:
        permissions.canShowAdminRatesLongTermTab &&
        permissions.canShowAdminRatesOnDemandTab,
    },
    {
      name: ORGANIZATION_ADMIN_MENU.SUPPLIERS.LABEL,
      iconOfKind: { icon: 'briefcaseMedical', kind: 'light' },
      redirectTo: ORGANIZATION_ADMIN_MENU.SUPPLIERS.ROUTE,
      canShow: permissions.canShowAdminSuppliers,
    },
    {
      name: ORGANIZATION_ADMIN_MENU.TEMPLATES__COMPLIANCE.LABEL,
      iconOfKind: { icon: 'copy', kind: 'light' },
      redirectTo: ORGANIZATION_ADMIN_MENU.TEMPLATES__COMPLIANCE.ROUTE,
      canShow:
        !permissions.canShowAdminStaffingRequestTemplates &&
        permissions.canShowAdminWorksListTemplates,
    },
    {
      name: ORGANIZATION_ADMIN_MENU.TEMPLATES__STAFFING_REQUEST.LABEL,
      iconOfKind: { icon: 'copy', kind: 'light' },
      redirectTo: ORGANIZATION_ADMIN_MENU.TEMPLATES__STAFFING_REQUEST.ROUTE,
      canShow:
        permissions.canShowAdminStaffingRequestTemplates &&
        !permissions.canShowAdminWorksListTemplates,
    },
    {
      name: 'Templates',
      iconOfKind: { icon: 'copy', kind: 'light' },
      children: [
        {
          name: ORGANIZATION_ADMIN_MENU.TEMPLATES__STAFFING_REQUEST.LABEL,
          redirectTo: ORGANIZATION_ADMIN_MENU.TEMPLATES__STAFFING_REQUEST.ROUTE,
        },
        {
          name: ORGANIZATION_ADMIN_MENU.TEMPLATES__COMPLIANCE.LABEL,
          redirectTo: ORGANIZATION_ADMIN_MENU.TEMPLATES__COMPLIANCE.ROUTE,
        },
      ],
      canShow:
        permissions.canShowAdminStaffingRequestTemplates &&
        permissions.canShowAdminWorksListTemplates,
    },
    {
      name: ORGANIZATION_ADMIN_MENU.TIMEKEEPING.LABEL,
      iconOfKind: { icon: 'timer', kind: 'light' },
      redirectTo: ORGANIZATION_ADMIN_MENU.TIMEKEEPING.ROUTE,
      canShow: permissions.canShowAdminTimekeepingHolidays,
    },
    {
      name: ORGANIZATION_ADMIN_MENU.USERS.LABEL,
      iconOfKind: { icon: 'users', kind: 'light' },
      redirectTo: ORGANIZATION_ADMIN_MENU.USERS.ROUTE,
      canShow: permissions.canShowAdminMembers,
    },
  ];
}

const NOT_FOUND_FALLBACK = '/works/not-found';

export function getFirstVisibleMenuItemRedirectTo(
  permissions: PermissionsType
) {
  const menuItems = getOrganizationAdminVerticalNavMenuItems(permissions);

  for (const item of menuItems) {
    if (!item.canShow) continue;
    if (item.redirectTo) return item.redirectTo;
    if (item.children?.[0]?.redirectTo) return item.children[0].redirectTo;
  }

  return NOT_FOUND_FALLBACK;
}
